export interface Role {
  id: number;
  name: string;
  description?: string;
}

export interface User {
  // Basic properties
  id: number;
  username: string;
  email: string;
  roles: string[] | Role[]; // Can be either string array or Role objects
  
  // Name properties from User entity in Java (camelCase)
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  
  // Properties from UserDto (lowercase first letter)
  // TypeScript needs these for type checking, but they'll be optional
  firstname?: string;
  lastname?: string;
  mobileNumber?: string;
  
  // Other possible properties
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  id: number;
  username: string;
  email: string;
  roles: string[];
}

export interface SignupRequest {
  username: string;
  email: string;
  password: string;
  roles?: string[];
}

export interface AuthContextType {
  auth: AuthState;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  signup: (signupData: SignupRequest) => Promise<void>;
  clearError: () => void;
}

// Utility functions for role handling
export const roleUtils = {
  // Extract role name from string or Role object
  getRoleName: (role: string | Role): string => {
    if (typeof role === 'string') {
      return role;
    }
    return role?.name || '';
  },

  // Get role names as string array from mixed roles
  getRoleNames: (roles: string[] | Role[]): string[] => {
    return roles.map(role => roleUtils.getRoleName(role));
  },

  // Check if user has a specific role
  hasRole: (userRoles: string[] | Role[], targetRole: string): boolean => {
    const roleNames = roleUtils.getRoleNames(userRoles);
    return roleNames.includes(targetRole);
  },

  // Get display name (removes ROLE_ prefix)
  getDisplayName: (role: string | Role): string => {
    const roleName = roleUtils.getRoleName(role);
    return roleName.replace('ROLE_', '');
  }
};